'use client';
import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const serviceLinks = [
  { label: 'Commercial Real Estate Finance', href: '/services/commercial-real-estate-finance' },
  { label: 'Asset-Based Lending', href: '/services/asset-based-lending' },
  { label: 'Financial Advisory Services', href: '/services/financial-advisory' },
  { label: 'Business Loan Management', href: '/services/business-loan-management' },
];

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services', hasDropdown: true },
  { label: 'Team', href: '#team' },
  { label: 'Contact', href: '#contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('Home');
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuHeight, setMenuHeight] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (menuRef.current) {
      setMenuHeight(menuOpen ? menuRef.current.scrollHeight : 0);
    }
  }, [menuOpen, mobileServicesOpen]);

  useEffect(() => {
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (menuOpen && menuRef.current && !menuRef.current.closest('header')?.contains(target)) {
        setMenuOpen(false);
      }
      if (servicesOpen && dropdownRef.current && !dropdownRef.current.contains(target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [menuOpen, servicesOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavClick = (label: string, href: string) => {
    setActiveLink(label);
    setMenuOpen(false);
    setServicesOpen(false);
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'bg-white shadow-lg shadow-brand-navy/10'
          : 'bg-white border-b border-gray-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-1 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); handleNavClick('Home', '#home'); }}
          className="flex items-center gap-3 group flex-shrink-0"
          aria-label="Go to home"
        >
          <div className="relative w-20 h-20 flex-shrink-0">
            <Image
              src="/assets/images/logo-1786968063771.png"
              alt="Grid Global Commercial Brokerage LLC Logo"
              fill
              className="object-contain"
              priority
            />
          </div>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <div key={link.label} className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className={`px-4 py-2 rounded-md font-sans font-medium text-sm transition-all duration-200 flex items-center gap-1 ${
                    activeLink === link.label
                      ? 'text-brand-blue bg-brand-blue/10' :'text-gray-600 hover:text-brand-navy hover:bg-gray-50'
                  }`}
                >
                  <strong>{link.label}</strong>
                  <svg
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {/* Dropdown */}
                {servicesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                    <a
                      href="#services"
                      onClick={(e) => { e.preventDefault(); handleNavClick('Services', '#services'); }}
                      className="block px-4 py-2.5 font-sans text-xs font-semibold text-brand-green uppercase tracking-widest border-b border-gray-100 mb-1"
                    >
                      All Services
                    </a>
                    {serviceLinks.map((svc) => (
                      <Link
                        key={svc.href}
                        href={svc.href}
                        onClick={() => { setServicesOpen(false); setActiveLink('Services'); }}
                        className="flex items-center gap-3 px-4 py-3 font-sans text-sm text-gray-700 hover:text-brand-navy hover:bg-gray-50 transition-colors group"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-green flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        {svc.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.label, link.href); }}
                className={`px-4 py-2 rounded-md font-sans font-medium text-sm transition-all duration-200 ${
                  activeLink === link.label
                    ? 'text-brand-blue bg-brand-blue/10' :'text-gray-600 hover:text-brand-navy hover:bg-gray-50'
                }`}
              >
                <strong>{link.label}</strong>
              </a>
            )
          )}
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNavClick('Contact', '#contact'); }}
            className="ml-3 px-5 py-2 bg-brand-navy text-white rounded-md font-sans font-medium text-sm hover:bg-brand-blue transition-all duration-200 hover:shadow-md hover:shadow-brand-blue/30"
          >
            Get Started
          </a>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 rounded-md text-brand-navy hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation select-none"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
        >
          <span
            className="block transition-transform duration-300"
            style={{ transform: menuOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
          >
            {menuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        ref={menuRef}
        className="md:hidden overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: menuHeight }}
        aria-hidden={!menuOpen}
      >
        <div className="bg-white border-t border-gray-100 shadow-lg">
          <nav
            className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col gap-1"
            aria-label="Mobile navigation"
          >
            {navLinks.map((link, index) =>
              link.hasDropdown ? (
                <div key={link.label}>
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-md font-sans font-medium text-sm transition-all duration-200 touch-manipulation ${
                      activeLink === link.label
                        ? 'text-brand-blue bg-brand-blue/10 font-semibold' :'text-gray-700 hover:text-brand-navy hover:bg-gray-50'
                    }`}
                    style={{
                      transitionDelay: menuOpen ? `${index * 40}ms` : '0ms',
                      opacity: menuOpen ? 1 : 0,
                    }}
                  >
                    {link.label}
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {mobileServicesOpen && (
                    <div className="ml-4 mt-1 flex flex-col gap-1 border-l-2 border-brand-green/30 pl-3">
                      <a
                        href="#services"
                        onClick={(e) => { e.preventDefault(); handleNavClick('Services', '#services'); }}
                        className="px-3 py-2 font-sans text-xs font-semibold text-brand-green uppercase tracking-widest"
                      >
                        All Services
                      </a>
                      {serviceLinks.map((svc) => (
                        <Link
                          key={svc.href}
                          href={svc.href}
                          onClick={() => { setMenuOpen(false); setMobileServicesOpen(false); }}
                          className="px-3 py-2 font-sans text-sm text-gray-600 hover:text-brand-navy rounded-md hover:bg-gray-50 transition-colors"
                        >
                          {svc.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(link.label, link.href); }}
                  className={`px-4 py-3 rounded-md font-sans font-medium text-sm transition-all duration-200 touch-manipulation active:scale-95 ${
                    activeLink === link.label
                      ? 'text-brand-blue bg-brand-blue/10 font-semibold' :'text-gray-700 hover:text-brand-navy hover:bg-gray-50 active:bg-gray-100'
                  }`}
                  style={{
                    transitionDelay: menuOpen ? `${index * 40}ms` : '0ms',
                    opacity: menuOpen ? 1 : 0,
                    transform: menuOpen ? 'translateX(0)' : 'translateX(-8px)',
                    transition: `opacity 250ms ease ${index * 40}ms, transform 250ms ease ${index * 40}ms, background-color 150ms ease, color 150ms ease`,
                  }}
                >
                  {link.label}
                </a>
              )
            )}
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); handleNavClick('Contact', '#contact'); }}
              className="mt-2 px-5 py-3 bg-brand-navy text-white rounded-md font-sans font-semibold text-sm text-center hover:bg-brand-blue active:bg-brand-blue/90 active:scale-95 transition-all duration-200 touch-manipulation"
              style={{
                transitionDelay: menuOpen ? `${navLinks.length * 40}ms` : '0ms',
                opacity: menuOpen ? 1 : 0,
                transform: menuOpen ? 'translateX(0)' : 'translateX(-8px)',
                transition: `opacity 250ms ease ${navLinks.length * 40}ms, transform 250ms ease ${navLinks.length * 40}ms, background-color 150ms ease`,
              }}
            >
              Get Started
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
}
